import { ALERT_FAMILIES, CASE_SEVERITIES, CASE_STATUSES, type CaseStatusKey } from '@nw/shared';

/**
 * W2-6-9 (D-126): the alerts center search — kept in the URL (`/alerts-center?view=&scope=&client=&sev=&fam=&status=&sent=&from=&to=&month=`).
 * Unknown values fall back to the defaults (view inbox, status open, sent all); lists are csv in the URL.
 */
export interface InboxSearch {
  view: 'inbox' | 'critical';
  /** a parent (network) or a client with no parent — GET /api/alert-cases/scopes */
  scope?: string;
  /** one branch (client) of the chosen parent */
  client?: string;
  sev: string[];
  fam: string[];
  status: CaseStatusKey | 'all';
  sent: 'all' | 'yes' | 'no';
  /** local YYYY-MM-DD, inclusive */
  from?: string;
  to?: string;
  /** YYYY-MM (critical view) */
  month?: string;
}

const DATE = /^\d{4}-(0[1-9]|1[0-2])-(0[1-9]|[12]\d|3[01])$/;
const MONTH = /^\d{4}-(0[1-9]|1[0-2])$/;
const ID = /^[\w-]{1,64}$/;

const listOf = (raw: unknown, allowed: readonly string[]): string[] => {
  const parts = Array.isArray(raw) ? raw.flatMap((x) => String(x).split(',')) : typeof raw === 'string' ? raw.split(',') : [];
  const out: string[] = [];
  for (const p of parts.map((s) => s.trim())) if (allowed.includes(p) && !out.includes(p)) out.push(p);
  return out;
};
const pick = <T extends string>(raw: unknown, allowed: readonly T[], fallback: T): T =>
  typeof raw === 'string' && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;
const match = (raw: unknown, re: RegExp): string | undefined => (typeof raw === 'string' && re.test(raw) ? raw : undefined);

export function parseInboxSearch(raw: Record<string, unknown>): InboxSearch {
  const s: InboxSearch = {
    view: pick(raw.view, ['inbox', 'critical'] as const, 'inbox'),
    sev: listOf(raw.sev, CASE_SEVERITIES),
    fam: listOf(raw.fam, ALERT_FAMILIES),
    status: pick(raw.status, [...CASE_STATUSES, 'all'] as const, 'open'),
    sent: pick(raw.sent, ['all', 'yes', 'no'] as const, 'all'),
  };
  const scope = match(raw.scope, ID);
  const client = match(raw.client, ID);
  const from = match(raw.from, DATE);
  const to = match(raw.to, DATE);
  const month = match(raw.month, MONTH);
  if (scope) s.scope = scope;
  if (client) s.client = client;
  if (from) s.from = from;
  if (to) s.to = to;
  if (month) s.month = month;
  return s;
}

/** the query of GET /api/alert-cases for this search (+ the next-page cursor) */
export function inboxApiQuery(s: InboxSearch, cursor?: string): string {
  const p = new URLSearchParams();
  if (s.scope) p.set('scope', s.scope);
  if (s.client) p.set('clientId', s.client);
  if (s.sev.length) p.set('severity', s.sev.join(','));
  if (s.fam.length) p.set('family', s.fam.join(','));
  p.set('status', s.status);
  p.set('sent', s.sent);
  if (s.from) p.set('from', s.from);
  if (s.to) p.set('to', s.to);
  if (cursor) p.set('cursor', cursor);
  return `?${p.toString()}`;
}

/** the URL form: defaults and empty values left out, lists as csv (parseInboxSearch reads it back) */
export function toUrlSearch(s: InboxSearch): Record<string, string> {
  const out: Record<string, string> = {};
  if (s.view !== 'inbox') out.view = s.view;
  if (s.scope) out.scope = s.scope;
  if (s.client) out.client = s.client;
  if (s.sev.length) out.sev = s.sev.join(',');
  if (s.fam.length) out.fam = s.fam.join(',');
  if (s.status !== 'open') out.status = s.status;
  if (s.sent !== 'all') out.sent = s.sent;
  if (s.from) out.from = s.from;
  if (s.to) out.to = s.to;
  if (s.month) out.month = s.month;
  return out;
}
